import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { createErrorClass } from '@canvas/error';
import { dirIsPositive, dirIsXAxis, type Direction } from '@canvas/math/direction';
import { intersectionPointOfTwoLines } from '@canvas/math/intersection-points';
import { vec2extended } from '@canvas/math/vec2';

import {
  computeGradientNormalizationTransform,
  type GraphicColorDefinition,
  type GraphicStrokeColorDefinition,
  type GraphicFillColorDefinition,
} from './color.ts';
import { type GraphicContent } from './graphic.ts';
import { getPointOnEdgeFromValueOnAxis } from './math.ts';
import { type Axis, GraphicPoint, type ReadonlyGraphicPoint } from './points.ts';
import {
  type GraphicCubicBezierDefinition,
  type GraphicLineDefinition,
  type GraphicSectionDefinition,
  type GraphicSegmentDefinition,
  type GraphicFillSectionDefinition,
  GraphicCubicBezier,
  type GraphicSegmentOptions,
} from './types.ts';

export { GraphicLinearGradientBuilder, GraphicRadialGradientBuilder } from './color.ts';

/**
 * Valid fill colors exclude fill-specific colors (can use stroke color).
 */
type ValidFillColor = Exclude<GraphicColorDefinition, GraphicFillColorDefinition>;

/**
 * Valid stroke colors exclude stroke-specific colors (can use fill color).
 */
type ValidStrokeColor = Exclude<GraphicColorDefinition, GraphicStrokeColorDefinition>;

/**
 * Options for the fill() method, excluding segments and ensuring only fill-compatible colors.
 */
type GraphicFillOptions = Omit<GraphicFillSectionDefinition, 'segments' | 'color'> & {
  color?: ValidFillColor;
};

/**
 * Options for the stroke() method, excluding segments and ensuring only stroke-compatible colors.
 */
type GraphicStrokeOptions = Omit<GraphicSectionDefinition, 'segments' | 'color'> & {
  color?: ValidStrokeColor;
};

export const GraphicsSectionIsEmptyError = createErrorClass(
  'GraphicsSectionIsEmptyError',
);
export const NoActiveGraphicsSectionError = createErrorClass(
  'NoActiveGraphicsSectionError',
);
export const RelativeOffsetForOffsetPointError = createErrorClass(
  'RelativeOffsetForOffsetPointError',
);
export const BezierAdjacentToRoundedCornerError = createErrorClass(
  'BezierAdjacentToRoundedCornerError',
);
export const ParallelLinesHaveNoIntersectionError = createErrorClass(
  'ParallelLinesHaveNoIntersectionError',
);
export const TextAreaSetMultipleTimesError = createErrorClass(
  'TextAreaSetMultipleTimesError',
);
export const NoIntersectionWithEdgeError = createErrorClass(
  'NoIntersectionWithEdgeError',
);
export const NotRepresentableByGraphicPointError = createErrorClass(
  'NotRepresentableByGraphicPointError',
);

type ReadablePoint = readonly [ReadablePointComponent, ReadablePointComponent];
type ReadablePointComponent = number | { readonly pos: number; readonly offset: number };

type ReadableBounds = {
  min: ReadablePoint;
  max: ReadablePoint;
};

type ReadableTextArea = {
  topLeft: ReadablePoint | GraphicPoint;
  bottomRight: ReadablePoint | GraphicPoint;
};

type RoundedCorner = {
  type: 'roundedCorner';
  start: GraphicPoint;
  end: GraphicPoint;
  radiusOverride?: number;
};

type GraphicSegmentForPostProcessing = GraphicSegmentDefinition | RoundedCorner;

type GraphicSectionForPostProcessing = {
  segments: GraphicSegmentForPostProcessing[];
  color?: GraphicColorDefinition;
};

type TextAreaGenerator = (
  sections: Pick<GraphicContent, 'fills' | 'strokes'>,
  bounds: Bounds,
) => ReadableTextArea;

// See https://pomax.github.io/bezierinfo/#circles_cubic for the derivation of this number
// ~= (4 / 3) * Math.tan(angleRads / 4)
const QUARTER_ARC_CONTROL_POINT_PROPORTION = 0.551785;
const QUARTER_ARC_INTERPOLATE_X: ReadonlyVec2 = [QUARTER_ARC_CONTROL_POINT_PROPORTION, 0];
const QUARTER_ARC_INTERPOLATE_Y: ReadonlyVec2 = [0, QUARTER_ARC_CONTROL_POINT_PROPORTION];

const getArcControlPointLengthCoefficient = (arcAngleRads: number) =>
  (4 / 3) * Math.tan(arcAngleRads / 4);

// We occasionally need up to 4 flyweights to call functions to calculate the
// intersection of two lines
const vecFlyweight1 = vec2.create();
const vecFlyweight2 = vec2.create();
const vecFlyweight3 = vec2.create();
const vecFlyweight4 = vec2.create();

const pointFlyweight = GraphicPoint.create();

export class GraphicBuilder {
  private cursor = GraphicPoint.create();
  private relativeOffsetPoint: { x?: number; y?: number } = {};

  private currentSubpathStartIndex = 0;
  private currentPath: GraphicSegmentForPostProcessing[] = [];

  private fills: GraphicSectionForPostProcessing[] = [];
  private strokes: GraphicSectionForPostProcessing[] = [];
  private textArea: TextAreaGenerator | undefined;

  private addSection<T extends GraphicSectionDefinition>(
    list: GraphicSectionForPostProcessing[],
    segments: GraphicSegmentForPostProcessing[],
    options: Omit<T, 'segments'> = {} as T,
  ) {
    list.push({ segments, ...options });
  }

  private appendToCurrentPath(item: GraphicSegmentForPostProcessing) {
    this.currentPath.push(item);
  }

  private checkCurrentFillCanBeTerminated() {
    // Check the fill is non-empty
    if (this.currentPath.length === 0) {
      throw new GraphicsSectionIsEmptyError(
        'You cannot terminate the current fill without adding any segments',
      );
    }

    const firstOp = this.currentPath[this.currentSubpathStartIndex];
    if (firstOp.type === 'subPathStart') {
      throw new GraphicsSectionIsEmptyError(
        'You cannot terminate the current fill without adding any segments',
      );
    }
  }

  private checkCurrentStrokeCanBeTerminated() {
    // Check the stroke is non-empty
    if (this.currentPath.length === 0) {
      throw new GraphicsSectionIsEmptyError(
        'You cannot terminate the current stroke without adding any segments',
      );
    }
  }

  private checkPointComponentHasNoOffset(c: ReadablePointComponent) {
    // Check the offset is not set or is zero
    if (typeof c !== 'number' && c.offset !== 0) {
      throw new RelativeOffsetForOffsetPointError(
        'You cannot offset a point with an existing offset relative to another point',
      );
    }
  }

  private preprocessPoint(out: GraphicPoint, p: ReadablePoint): GraphicPoint {
    this.preprocessComponent(out, 'x', p[0]);
    this.preprocessComponent(out, 'y', p[1]);

    return out;
  }

  private preprocessComponent(
    out: GraphicPoint,
    axis: Axis,
    c: ReadablePointComponent,
  ): GraphicPoint {
    // If no relative offset point is specified for this axis, convert directly
    // to a GraphicPoint.
    if (this.relativeOffsetPoint[axis] === undefined) {
      return typeof c === 'number'
        ? GraphicPoint.setForAxis(out, axis, c, 0)
        : GraphicPoint.setForAxis(out, axis, c.pos, c.offset);
    }

    // If a relative offset point is specified, use it for the position and set
    // the offset instead. See setRelativeOffsetPoint for details.
    this.checkPointComponentHasNoOffset(c);

    const pos = typeof c === 'number' ? c : c.pos;
    const relativeTo = this.relativeOffsetPoint[axis];

    return GraphicPoint.setForAxis(out, axis, relativeTo, pos - relativeTo);
  }

  private appendLine(p: GraphicPoint, options?: GraphicSegmentOptions): void {
    // If the line has 0 length, don't add a segment
    if (GraphicPoint.equals(this.cursor, p)) {
      return;
    }

    this.appendToCurrentPath({
      type: 'line',
      start: GraphicPoint.copy(GraphicPoint.create(), this.cursor),
      end: p,
      options,
    });

    GraphicPoint.copy(this.cursor, p);
  }

  private appendCubicBezier(
    cp1: GraphicPoint,
    cp2: GraphicPoint,
    p: GraphicPoint,
    options?: GraphicSegmentOptions,
  ): void {
    this.appendToCurrentPath({
      type: 'cubicBezier',
      start: GraphicPoint.copy(GraphicPoint.create(), this.cursor),
      cp1,
      cp2,
      end: p,
      options,
    });

    GraphicPoint.copy(this.cursor, p);
  }

  private appendQuarterEllipse(
    direction: 'concave-up' | 'concave-down',
    p: GraphicPoint,
  ): void {
    const isIncreaseInY =
      p.resolveAtUnitSizeForAxis('y') >= this.cursor.resolveAtUnitSizeForAxis('y');

    // Create curvature by interpolating each control point along only one axis
    // relative to an endpoint
    let cp1Proportions: ReadonlyVec2;
    let cp2Proportions: ReadonlyVec2;
    if (
      (!isIncreaseInY && direction === 'concave-up') ||
      (isIncreaseInY && direction === 'concave-down')
    ) {
      cp1Proportions = QUARTER_ARC_INTERPOLATE_X;
      cp2Proportions = QUARTER_ARC_INTERPOLATE_Y;
    } else {
      cp1Proportions = QUARTER_ARC_INTERPOLATE_Y;
      cp2Proportions = QUARTER_ARC_INTERPOLATE_X;
    }

    // Create control points
    const cp1 = GraphicPoint.create();
    GraphicPoint.interpolateByAxis(cp1, this.cursor, p, cp1Proportions);

    const cp2 = GraphicPoint.create();
    GraphicPoint.interpolateByAxis(cp2, p, this.cursor, cp2Proportions);

    this.appendCubicBezier(cp1, cp2, p);
  }

  private appendRoundedCorner(p: GraphicPoint, radiusOverride?: number): void {
    this.appendToCurrentPath({
      type: 'roundedCorner',
      start: GraphicPoint.copy(GraphicPoint.create(), this.cursor),
      end: p,
      radiusOverride: radiusOverride,
    });
    GraphicPoint.copy(this.cursor, p);
  }

  /**
   * Begins a new path for the graphic.
   */
  beginPath(): this {
    GraphicPoint.zero(this.cursor);
    this.currentSubpathStartIndex = 0;
    this.currentPath.length = 0;

    return this;
  }

  /**
   * Fills the current path, adding it to the list of fills.
   *
   * @returns This builder for method chaining
   */
  fill(fill?: GraphicFillOptions): this {
    this.checkCurrentFillCanBeTerminated();

    this.addSection(this.fills, [...this.currentPath], fill);

    return this;
  }

  /**
   * Strokes the current path, adding it to the list of strokes.
   *
   * @returns This builder for method chaining
   */
  stroke(stroke?: GraphicStrokeOptions): this {
    this.checkCurrentStrokeCanBeTerminated();

    this.addSection(this.strokes, [...this.currentPath], stroke);

    return this;
  }

  /**
   * Moves to a specified point without creating a segment. If there's a
   * currently-active path, this will start a new sub-path.
   *
   * @param p The point to move to
   * @returns This builder for method chaining
   */
  moveTo(p: ReadablePoint): this {
    this.preprocessPoint(this.cursor, p);

    if (this.currentPath.length > 0) {
      const lastSegment = this.currentPath.at(-1) as GraphicSegmentForPostProcessing;
      if (lastSegment.type !== 'subPathStart') {
        // A path has already been started, so we need to mark that this is a new sub-path
        this.appendToCurrentPath({
          type: 'subPathStart',
          start: GraphicPoint.copy(GraphicPoint.create(), this.cursor),
        });
        this.currentSubpathStartIndex = this.currentPath.length;
      } else {
        // If the last segment was a subPathStart, we should update its start
        GraphicPoint.copy(lastSegment.start, this.cursor);
      }
    }

    return this;
  }

  /**
   * Creates a straight line from the previous point to the specified point.
   *
   * @param p The endpoint of the line
   * @returns This builder for method chaining
   */
  lineTo(p: ReadablePoint, options?: GraphicSegmentOptions): this {
    this.appendLine(this.preprocessPoint(GraphicPoint.create(), p), options);
    return this;
  }

  /**
   * Creates a horizontal line from the previous point to the specified
   * x-coordinate.
   *
   * @param x The x-coordinate of the endpoint of the line
   * @returns This builder for method chaining
   */
  horizontalTo(x: ReadablePointComponent): this {
    const p = GraphicPoint.copy(GraphicPoint.create(), this.cursor);
    this.preprocessComponent(p, 'x', x);
    this.appendLine(p);

    return this;
  }

  /**
   * Creates a vertical line from the previous point to the specified
   * y-coordinate.
   *
   * @param y The y-coordinate of the endpoint of the line
   * @returns This builder for method chaining
   */
  verticalTo(y: ReadablePointComponent): this {
    const p = GraphicPoint.copy(GraphicPoint.create(), this.cursor);
    this.preprocessComponent(p, 'y', y);
    this.appendLine(p);

    return this;
  }

  /**
   * Creates a cubic bezier curve from the previous point to the specified point.
   * See {@link file://./../../bezier/src/bezier.ts} for details of our bezier
   * implementation.
   *
   * @param cp1 The bezier control point closest to the previous point
   * @param cp2 The bezier control point closest to the endpoint
   * @param p The endpoint of the curve
   * @returns This builder for method chaining
   */
  cubicBezierTo(
    cp1: ReadablePoint,
    cp2: ReadablePoint,
    p: ReadablePoint,
    options?: GraphicSegmentOptions,
  ): this {
    this.appendCubicBezier(
      this.preprocessPoint(GraphicPoint.create(), cp1),
      this.preprocessPoint(GraphicPoint.create(), cp2),
      this.preprocessPoint(GraphicPoint.create(), p),
      options,
    );
    return this;
  }

  /**
   * Creates a quadratic bezier curve from the previous point to the specified point.
   * See {@link file://./../../bezier/src/bezier.ts} for details of our bezier
   * implementation.
   *
   * @param cp The bezier control point
   * @param p The endpoint of the curve
   * @returns This builder for method chaining
   */
  quadraticBezierTo(cp: ReadablePoint, p: ReadablePoint): this {
    const quadraticCp = this.preprocessPoint(pointFlyweight, cp);
    const end = this.preprocessPoint(GraphicPoint.create(), p);

    // Convert the quadratic bezier to a cubic bezier by calculating equivalent control
    // points. See: https://fontforge.org/docs/techref/bezier.html#converting-truetype-to-postscript
    this.appendCubicBezier(
      GraphicPoint.interpolate(GraphicPoint.create(), this.cursor, quadraticCp, 2 / 3),
      GraphicPoint.interpolate(GraphicPoint.create(), end, quadraticCp, 2 / 3),
      end,
    );

    return this;
  }

  /**
   * Creates a quarter ellipse from the previous point to the specified point.
   * The arc goes from one cardinal direction to another; you cannot specify an
   * arc with start or end gradient other than in `pi/2` increments with this
   * method.
   *
   * As there are two ways to draw such an arc between any two points that
   * don't lie on the same axis, you must specify the concavity to distinguish.
   *
   * e.g.
   * ```
   * For points       Concave up       Concave down
   * x                x                x────────\
   *                  |                          \
   *                   \                          |
   *            x       \────────x                x
   * ```
   *
   * @param direction The concavity of the arc
   * @param end The endpoint of the arc
   * @returns This builder for method chaining
   */
  quarterEllipse(direction: 'concave-up' | 'concave-down', p: ReadablePoint): this {
    this.appendQuarterEllipse(direction, this.preprocessPoint(GraphicPoint.create(), p));
    return this;
  }

  /**
   * Creates a horizontally/vertically aligned half-ellipse from the previous
   * point to the specified point.
   *
   * A horizontal ellipse goes through the points
   * `(startX, startY), ((startX + endX) / 2, midpointY), (endX, startY)`,
   * a vertical ellipse goes through
   * `(startX, startY), (midpointX, (startY + endY) / 2), (startX, endY)`.
   *
   * @param params The direction (horizontal or vertical), identifying midpoint
   * coordinate, and identifying end coordinate
   *
   * @returns This builder for method chaining
   */
  halfEllipse(
    params:
      | {
          direction: 'horizontal';
          midpointY: ReadablePointComponent;
          endX: ReadablePointComponent;
        }
      | {
          direction: 'vertical';
          midpointX: ReadablePointComponent;
          endY: ReadablePointComponent;
        },
  ): this {
    const midpoint = GraphicPoint.create();
    const end = GraphicPoint.create();

    if (params.direction === 'horizontal') {
      // Populate endpoint
      GraphicPoint.copy(end, this.cursor);
      this.preprocessComponent(end, 'x', params.endX);

      // Populate midpoint
      GraphicPoint.interpolate(midpoint, this.cursor, end, 0.5);
      this.preprocessComponent(midpoint, 'y', params.midpointY);

      // Determine concavity
      const concavity =
        midpoint.resolveAtUnitSizeForAxis('y') > this.cursor.resolveAtUnitSizeForAxis('y')
          ? 'concave-up'
          : 'concave-down';

      // Create arcs
      this.appendQuarterEllipse(concavity, midpoint);
      this.appendQuarterEllipse(concavity, end);
    } else {
      // Populate endpoint
      GraphicPoint.copy(end, this.cursor);
      this.preprocessComponent(end, 'y', params.endY);

      // Populate midpoint
      GraphicPoint.interpolate(midpoint, this.cursor, end, 0.5);
      this.preprocessComponent(midpoint, 'x', params.midpointX);

      // Determine concavity
      const isIncreaseInY =
        end.resolveAtUnitSizeForAxis('y') >= this.cursor.resolveAtUnitSizeForAxis('y');

      // Create arcs
      this.appendQuarterEllipse(isIncreaseInY ? 'concave-down' : 'concave-up', midpoint);
      this.appendQuarterEllipse(isIncreaseInY ? 'concave-up' : 'concave-down', end);
    }

    return this;
  }

  /**
   * Creates a rounded corner from the previous point to the specified point.
   * Rounded corners are represented by circular arcs, so the start and
   * endpoints may be shifted to create an arc with the appropriate radius.
   *
   * @param p The endpoint of the rounded corner
   * @param radiusOverride An override for the radius of the corner. This will
   * otherwise be automatically determined from the start and endpoints.
   *
   * @returns This builder for method chaining
   */
  roundedCornerTo(p: ReadablePoint, radiusOverride?: number): this {
    this.appendRoundedCorner(
      this.preprocessPoint(GraphicPoint.create(), p),
      radiusOverride,
    );
    return this;
  }

  /**
   * Creates a rounded corner from the current point to the next point.
   * Rounded corners are represented by circular arcs, so the start and
   * endpoints may be shifted to create an arc with the appropriate radius.
   * @param radiusOverride An override for the radius of the corner. This will
   * otherwise be automatically determined from the start and endpoints.
   * @returns This builder for method chaining
   */
  roundedCorner(radiusOverride: number): this {
    const lastSegment = this.currentPath.at(-1);
    if (!lastSegment || lastSegment.type === 'subPathStart') {
      throw new Error('You cannot call roundedCorner without a previous point');
    }
    const p = lastSegment.end;
    this.appendRoundedCorner(p, radiusOverride);
    return this;
  }

  /**
   * Creates a rounded rectangle from the previous point to the specified point.
   * The corners are set at a fixed radius and so do not change size as the graphic is scaled.
   *
   *
   * @param topLeftCorner The top left corner of the rectangle
   * @param bottomRightCorner The bottom right corner of the rectangle
   * @param radiusOverride The radius of the corners
   * @returns This builder for method chaining
   */
  roundedRectangle(
    topLeftCorner: ReadablePoint,
    bottomRightCorner: ReadablePoint,
    radiusOverride: number,
  ): this {
    this.moveTo(topLeftCorner)
      // Top left corner
      .horizontalTo(bottomRightCorner[0])
      .roundedCorner(radiusOverride)
      // Top right corner
      .verticalTo(bottomRightCorner[1])
      .roundedCorner(radiusOverride)
      // Bottom right corner
      .horizontalTo(topLeftCorner[0])
      .roundedCorner(radiusOverride)
      // Bottom left corner
      .verticalTo(topLeftCorner[1])
      .roundedCorner(radiusOverride)
      .close();
    return this;
  }

  /**
   * Draws a circle centered at the specified point with the given radius.
   *
   * @param cx - The x coordinate of the center of the circle
   * @param cy - The y coordinate of the center of the circle
   * @param radius - The radius of the circle
   * @returns This builder for method chaining
   */
  circle(cx: number, cy: number, radius: number): this {
    this.moveTo([cx + radius, cy])
      .halfEllipse({ direction: 'horizontal', midpointY: cy + radius, endX: cx - radius })
      .halfEllipse({ direction: 'horizontal', midpointY: cy - radius, endX: cx + radius })
      .close();
    return this;
  }

  /**
   * Draws a straight line from the current point to the start point of an
   * active section. If only one of fill and stroke is currently active, that
   * section will determine the endpoint of the line. If both are active, the
   * start point of the active fill will be the endpoint.
   *
   * @returns This builder for method chaining
   */
  close(): this {
    if (this.currentPath.length === 0) {
      throw new GraphicsSectionIsEmptyError('You cannot close an empty path');
    }

    const firstOp = this.currentPath[this.currentSubpathStartIndex];
    if (firstOp.type === 'subPathStart') {
      throw new GraphicsSectionIsEmptyError('You cannot close an empty path');
    }

    this.appendLine(firstOp.start);
    return this;
  }

  /**
   * Specifies that all subsequent points without offset given to the builder
   * should be instead treated as having the position of the relative point
   * provided to this function, and an offset that places it at the same
   * position as it would be from its original position value, at the scale
   * used when defining the graphic. Phrased alternatively, it prevents the
   * scaling with graphic size of subsequent points on the provided axis.
   *
   * e.g.
   * ```
   * .setRelativeOffsetPoint({ x: 5 })
   * .lineTo([15, 0])
   * ```
   * will draw a line to a point with x-position 5 and x-offset 10.
   *
   * Relative offset points will persist until the function is called again. To
   * clear offset points, pass the empty object as a parameter, i.e.
   * `.setRelativeOffsetPoint({})`. While an offset point for an axis is set,
   * the builder will throw an error if you try to provide a point to a builder
   * function with an explicitly set offset.
   *
   * @param relativeToPoint The point that subsequent points should be created
   * with an offset relative to. The x-coordinate, y-coordinate, both or
   * neither can be provided.
   *
   * @returns This builder for method chaining
   */
  setRelativeOffsetPoint(relativeToPoint: typeof this.relativeOffsetPoint): this {
    this.relativeOffsetPoint = relativeToPoint;
    return this;
  }

  /**
   * Sets the text area for the GraphicContent. If you provide a function as
   * the text area, the static value will be determined when @see build is
   * called, and given the fill and stroke definitions and bounds to enable
   * determination of the text area from the actual shape of the graphic.
   *
   * @param textArea Either an object specifying the bounds of the text area,
   * or a function to calculate these bounds from the fills and strokes of the
   * graphic and their bounds
   *
   * @returns This builder for method chaining
   */
  setTextArea(textArea: ReadableTextArea | TextAreaGenerator): this {
    if (this.textArea) {
      throw new TextAreaSetMultipleTimesError(
        'Text area has already been set for this builder',
      );
    }

    this.textArea = 'topLeft' in textArea ? () => textArea : textArea;

    return this;
  }

  /**
   * Returns a {@link GraphicContent} object that can be used to populate a Graphic with
   * the fills and strokes created in this builder.
   *
   * @param trimming Describes how coordinates of the graphic's segments should be
   * normalized. If `none`, the coordinates will be used as-is. If `trim`, will remove
   * padding from the top and left of the graphic. `trimAndNormalise`, will also re-scale
   * coordinates to be in the range 0 -> 1. See {@link trim} for details.
   * @param boundsOverride The bounds of the graphic, used when trimming or normalizing. If not
   * provided, the bounds will be calculated from the fills and strokes.
   *
   * @returns The {@link GraphicContent} for use in Graphic construction.
   */
  build(
    trimming: 'none' | 'trim' | 'trimAndNormalise' = 'none',
    boundsOverride?: ReadableBounds,
  ): GraphicContent {
    // Content without text area yet
    let content = postProcess({
      fills: this.fills,
      strokes: this.strokes,
    });

    // Get the bounds from the fills and strokes if not provided. Note that the text area
    // is not included in the content yet and so not part of the bounds calculation.
    let bounds: Bounds;
    if (boundsOverride) {
      const min = readablePointToGraphicPoint(boundsOverride.min);
      const max = readablePointToGraphicPoint(boundsOverride.max);
      const size = GraphicPoint.subtract(GraphicPoint.create(), max, min);
      bounds = { min, max, size };
    } else {
      bounds = getBounds(content);
    }

    // Add the text area based on the provided fills, strokes and bounds
    if (this.textArea) {
      const readableTextArea = this.textArea(content, bounds);
      content.textArea = {
        topLeft:
          readableTextArea.topLeft instanceof GraphicPoint
            ? readableTextArea.topLeft
            : readablePointToGraphicPoint(readableTextArea.topLeft),
        bottomRight:
          readableTextArea.bottomRight instanceof GraphicPoint
            ? readableTextArea.bottomRight
            : readablePointToGraphicPoint(readableTextArea.bottomRight),
      };
    }

    // Remove surrounding whitespace
    if (trimming !== 'none') {
      content = trim(content, bounds, trimming === 'trimAndNormalise');
    }

    return content;
  }
}

// Post processing

/**
 * Takes graphics content described to the builder and performs any
 * post-processing steps that require the entire graphic to be defined, such as
 * rounding corners.
 *
 * @param content Content prior to post-processing
 * @returns A GraphicContent object that can be used to define a Graphic
 */
function postProcess(content: {
  fills: GraphicSectionForPostProcessing[];
  strokes: GraphicSectionForPostProcessing[];
}): GraphicContent {
  const fills = content.fills.map(postProcessSection);
  const strokes = content.strokes.map(postProcessSection);

  return { fills, strokes };
}

/**
 * Performs the post-processing operations described by @see postProcess on a
 * single fill or stroke. This is a destructive operation.
 *
 * @param section The fill or stroke to process
 * @returns The section after post-processing
 */
function postProcessSection(
  section: GraphicSectionForPostProcessing,
): GraphicSectionDefinition {
  for (let i = 0; i < section.segments.length; i++) {
    if (section.segments[i].type === 'roundedCorner') {
      replaceRoundedCornerWithBeziers(section.segments, i);
    }
  }

  return section as GraphicSectionDefinition;
}

/**
 * Given a list of segments and an index, returns the last segment before this
 * index, accounting for list wrapping and division into sub-paths.
 *
 * @param segments The full list of segments
 * @param currentIndex The index of the current segment
 * @returns The previous segment in this sub-path, accounting for wraparound
 */
function getPreviousSegmentInSubpath(
  segments: GraphicSegmentForPostProcessing[],
  currentIndex: number,
): GraphicSegmentForPostProcessing {
  // If this isn't the first segment in the list or after a sub-path start,
  // return the previous segment
  if (currentIndex - 1 >= 0 && segments[currentIndex - 1].type !== 'subPathStart') {
    return segments[currentIndex - 1];
  }

  // If there are no sub-path breaks in the list, return the last segment (as
  // we must be at the start of the list)
  if (!segments.some((segment) => segment.type === 'subPathStart')) {
    return segments[segments.length - 1];
  }

  // Otherwise, find the next sub-path start or end of the list, and return
  // the first segment before it
  let subPathStartIdx = currentIndex + 1;
  while (
    subPathStartIdx < segments.length &&
    segments[subPathStartIdx].type !== 'subPathStart'
  ) {
    subPathStartIdx++;
  }

  return segments[subPathStartIdx - 1];
}

/**
 * Given a list of segments and an index, returns the next segment after this
 * index, accounting for list wrapping and division into sub-paths.
 *
 * @param segments The full list of segments
 * @param currentIndex The index of the current segment
 * @returns The next segment in this sub-path, accounting for wraparound
 */
function getNextSegmentInSubpath(
  segments: GraphicSegmentForPostProcessing[],
  currentIndex: number,
): GraphicSegmentForPostProcessing {
  // If this isn't the last segment before the end of the list or a sub-path
  // start, return the next segment
  if (
    currentIndex + 1 < segments.length &&
    segments[currentIndex + 1].type !== 'subPathStart'
  ) {
    return segments[currentIndex + 1];
  }

  // If there are no sub-path breaks in the list, return the first segment (as
  // we must be at the end of the list)
  if (!segments.some((segment) => segment.type === 'subPathStart')) {
    return segments[0];
  }

  // Otherwise, find the last sub-path start or start of the list, and return
  // the first segment after it
  let subPathStartIdx = currentIndex - 1;
  while (subPathStartIdx > -1 && segments[subPathStartIdx].type !== 'subPathStart') {
    subPathStartIdx--;
  }

  return segments[subPathStartIdx + 1];
}

/**
 * Given a list of segments and the index of a rounded corner, replaces the
 * rounded corner with an approximating bezier curve and modifies the start and
 * endpoints of the adjacent segments to match the rounded corner's radius.
 *
 * @param segments The full list of segments for a section
 * @param index The index of a rounded corner in the list of segments
 */
function replaceRoundedCornerWithBeziers(
  segments: GraphicSegmentForPostProcessing[],
  index: number,
): void {
  // Handle wraparound when the first or last segment is a rounded corner
  const prevSegment = getPreviousSegmentInSubpath(segments, index);
  const nextSegment = getNextSegmentInSubpath(segments, index);

  if (prevSegment.type !== 'line' || nextSegment.type !== 'line') {
    throw new BezierAdjacentToRoundedCornerError(
      'Rounded corners must be connected to lines only',
    );
  }

  // Determine intersection based on resolution at unit size. While this means
  // we later have to convert back to GraphicPoints, the intersection of two
  // GraphicLines is usually not mathematically representable as a GraphicPoint.
  const resolvedPrevStart = prevSegment.start.resolveAtUnitSize(vecFlyweight1);
  const resolvedPrevEnd = prevSegment.end.resolveAtUnitSize(vecFlyweight2);
  const resolvedNextStart = nextSegment.start.resolveAtUnitSize(vecFlyweight3);
  const resolvedNextEnd = nextSegment.end.resolveAtUnitSize(vecFlyweight4);

  const intersection = intersectionPointOfTwoLines(
    resolvedPrevStart,
    resolvedPrevEnd,
    resolvedNextStart,
    resolvedNextEnd,
    true,
  )?.point;

  if (intersection === undefined) {
    throw new ParallelLinesHaveNoIntersectionError(
      'No intersection exists for parallel lines, so an appropriately rounded corner cannot be determined',
    );
  }

  const prevAngle = vec2extended.angleFrom(resolvedPrevStart, resolvedPrevEnd);
  const nextAngle = vec2extended.angleFrom(resolvedNextStart, resolvedNextEnd);

  const tau = Math.PI * 2;

  // Calculate the angle between the two adjacent lines
  let deltaAngle = (Math.PI - nextAngle + prevAngle) % tau;
  if (deltaAngle > Math.PI) {
    deltaAngle -= tau;
  } else if (deltaAngle < -Math.PI) {
    deltaAngle += tau;
  }

  const radiusOverride = (segments[index] as RoundedCorner).radiusOverride;
  let offsetFromIntersection: number;
  let radius: number;

  // If a radius override has been specified, we use that to calculate how far
  // from the intersection point our arc should begin. Otherwise, we take the
  // average distance of the adjacent line endpoints from the intersection.
  if (radiusOverride !== undefined) {
    radius = radiusOverride;
    offsetFromIntersection = radius / Math.tan(Math.abs(deltaAngle) / 2);
  } else {
    offsetFromIntersection =
      (vec2.distance(intersection, resolvedPrevEnd) +
        vec2.distance(intersection, resolvedNextStart)) /
      2;
    radius = offsetFromIntersection * Math.tan(Math.abs(deltaAngle) / 2);
  }

  // Determine a GraphicPoint representation of the points at the required
  // distances from the intersection point, based on each segment
  const curveStart = determineRoundedCornerEndpoint(
    prevSegment,
    nextSegment,
    intersection,
    [
      Math.cos(prevAngle) * -offsetFromIntersection,
      Math.sin(prevAngle) * -offsetFromIntersection,
    ],
  );

  const curveEnd = determineRoundedCornerEndpoint(
    nextSegment,
    prevSegment,
    intersection,
    [
      Math.cos(nextAngle) * offsetFromIntersection,
      Math.sin(nextAngle) * offsetFromIntersection,
    ],
  );

  // Modify the adjacent segments to match the start and endpoint of the arc
  prevSegment.end = curveStart;
  nextSegment.start = curveEnd;

  // The angle of the arc is opposite the angle of intersection of the two edges
  const arcAngle = Math.PI - Math.abs(deltaAngle);
  const cpLength = radius * getArcControlPointLengthCoefficient(arcAngle);

  const cp1 = extendLine(prevSegment, cpLength);
  const cp2 = extendLine(nextSegment, -cpLength);

  segments[index] = {
    type: 'cubicBezier',
    start: curveStart,
    cp1,
    cp2,
    end: curveEnd,
  };
}

/**
 * Given two intersecting segments and an offset from the intersection along
 * the first segment, returns the best graphic point representation of that
 * location for use as one endpoint of a rounded corner.
 *
 * @param segment The segment the final point lies on
 * @param intersectingSegment A segment intersecting the first
 * @param resolvedIntersection The intersection, resolved at unit scale
 * @param offsetFromIntersection The offset of the final point from the intersection
 * @returns A graphic point representing one endpoint of a rounded corner
 */
function determineRoundedCornerEndpoint(
  segment: GraphicLineDefinition,
  intersectingSegment: GraphicLineDefinition,
  resolvedIntersection: ReadonlyVec2,
  offsetFromIntersection: ReadonlyVec2,
): GraphicPoint {
  const somePointOnSegment = graphicPointToReadablePoint(segment.start);
  const nonScalingOffset = GraphicPoint.fromOffsetsOnly(
    pointFlyweight,
    offsetFromIntersection,
  );

  if (GraphicPoint.isHorizontal(segment.start, segment.end)) {
    const intersection = getHorizontalIntersectionWithEdge(
      intersectingSegment,
      somePointOnSegment[1],
      true,
    );
    return GraphicPoint.add(intersection, intersection, nonScalingOffset);
  } else if (GraphicPoint.isVertical(segment.start, segment.end)) {
    const intersection = getVerticalIntersectionWithEdge(
      intersectingSegment,
      somePointOnSegment[0],
      true,
    );
    return GraphicPoint.add(intersection, intersection, nonScalingOffset);
  } else {
    const resolvedEndpoint = vec2.add(
      vecFlyweight1,
      resolvedIntersection,
      offsetFromIntersection,
    );
    return unresolvePointOnLine(resolvedEndpoint, segment);
  }
}

// Trimming and normalisation

type Bounds = {
  min: ReadonlyGraphicPoint;
  max: ReadonlyGraphicPoint;
  size: ReadonlyGraphicPoint;
};

/**
 * Trims a {@link GraphicContent} by shifting the coordinates of all points within the
 * fills and strokes to the origin. This effectively removes padding from the top and
 * left of the shape. If {@link shouldNormalise} is specified, also scales these
 * coordinates to have a range of 0 -> 1.
 *
 * @param original The {@link GraphicContent} to trim.
 * @param bounds The bounds to use for trimming and normalisation. The top-left of these
 * bounds defines the amount to shift the coordinates by, and the size defines the
 * range to scale the coordinates to.
 * @param shouldNormalise If `true`, will scale coordinates within the content to a range
 * of 0 -> 1.
 * @returns A new {@link GraphicContent} with the shifted coordinates.
 */
function trim(
  original: GraphicContent,
  bounds: Bounds,
  shouldNormalise: boolean,
): GraphicContent {
  return {
    fills: original.fills.map((fill) => trimSection(fill, bounds, shouldNormalise)),
    strokes: original.strokes.map((stroke) =>
      trimSection(stroke, bounds, shouldNormalise),
    ),
    textArea: original.textArea
      ? trimTextArea(original.textArea, bounds, shouldNormalise)
      : undefined,
  };
}

/**
 * Performs the trimming operations described by @see trim on a single fill or
 * stroke.
 *
 * @param section The fill or stroke to trim
 * @param bounds The bounds of the entire GraphicContent
 * @param shouldNormalise Whether to also normalise coordinates to a range of 0 -> 1
 * @returns A new section with the shifted coordinates
 */
function trimSection(
  section: GraphicSectionDefinition,
  bounds: Bounds,
  shouldNormalise: boolean,
): GraphicSectionDefinition {
  const { segments, color, ...rest } = section;
  const newSection: GraphicSectionDefinition = { segments: [], color, ...rest };

  for (const segment of segments) {
    switch (segment.type) {
      case 'cubicBezier':
        newSection.segments.push({
          type: 'cubicBezier',
          start: trimPoint(segment.start, bounds, shouldNormalise),
          cp1: trimPoint(segment.cp1, bounds, shouldNormalise),
          cp2: trimPoint(segment.cp2, bounds, shouldNormalise),
          end: trimPoint(segment.end, bounds, shouldNormalise),
          options: segment.options,
        });
        break;
      case 'line':
        newSection.segments.push({
          type: 'line',
          start: trimPoint(segment.start, bounds, shouldNormalise),
          end: trimPoint(segment.end, bounds, shouldNormalise),
          options: segment.options,
        });
        break;
      case 'subPathStart':
        newSection.segments.push({
          type: 'subPathStart',
          start: trimPoint(segment.start, bounds, shouldNormalise),
        });
        break;
    }
  }

  // If the section has a gradient, we also need to trim/normalize it
  if (
    newSection.color?.type === 'linear-gradient' ||
    newSection.color?.type === 'radial-gradient'
  ) {
    newSection.color = {
      ...newSection.color,
      transform: computeGradientNormalizationTransform(
        bounds.min,
        bounds.size,
        shouldNormalise,
        newSection.color.transform,
      ),
    };
  }

  return newSection;
}

/**
 * Performs the trimming operations described by @see trim on a text area.
 *
 * @param textArea The text area to trim
 * @param bounds The bounds of the entire GraphicContent
 * @param shouldNormalise Whether to also normalise coordinates to a range of 0 -> 1
 * @returns A new text area with the shifted coordinates
 */
function trimTextArea(
  textArea: NonNullable<GraphicContent['textArea']>,
  bounds: Bounds,
  shouldNormalise: boolean,
): NonNullable<GraphicContent['textArea']> {
  return {
    topLeft: trimPoint(textArea.topLeft, bounds, shouldNormalise),
    bottomRight: trimPoint(textArea.bottomRight, bounds, shouldNormalise),
  };
}

/**
 * Performs the trimming operations described by @see trim on a single point.
 *
 * @param p The point to trim
 * @param bounds The bounds of the entire GraphicContent
 * @param shouldNormalise Whether to also normalise coordinates to a range of 0 -> 1
 * @returns A new point with the shifted coordinates
 */
function trimPoint(
  p: ReadonlyGraphicPoint,
  bounds: Bounds,
  shouldNormalise: boolean,
): GraphicPoint {
  // Trim top-left empty space
  const newPoint = GraphicPoint.subtract(GraphicPoint.create(), p, bounds.min);

  if (shouldNormalise) {
    // If the outermost point has a non-zero offset, adjust all points to
    // ensure that point stays snapped to the edge of the bounding box, without
    // affecting the appearance of the shape. The new outermost point will have
    // a position of `bounds.size.pos + bounds.size.offset`, and an offset of 0.
    adjustPointSoBottomRightHasOffsetOfZero(newPoint, bounds.size);

    // Normalise
    GraphicPoint.dividePosition(
      newPoint,
      newPoint,
      bounds.size.resolveAtUnitSize(vecFlyweight1), // divide by `bounds.size.pos + bounds.size.offset` so outermost point has position of 1
    );
  }

  return newPoint;
}

/**
 * Adjust so that outermost points on the bottom-right have an offset of 0.
 *
 * Derivation:
 *
 * We've already subtracted off the min value, so we know our points lie in
 * the interval [0, bounds.size]. We have 3 constraints we need to satisfy:
 * - We want the outermost point to have an offset of 0:
 *     `shift(bounds.size).offset = 0` (1)
 * - At the scale of the definition, no point should change location:
 *     `shift(p).resolveAtUnitSize() = p.resolveAtUnitSize()` (2)
 * - After normalisation the point should scale at the same rate as if it had not been shifted:
 *     `normalise_shifted(shift(p)).pos = normalise(p).pos` (3)
 *
 * First, consider (2):
 * ```
 * shift(p).resolveAtUnitSize() = p.resolveAtUnitSize()
 * -> shift(p).pos + shift(p).offset = p.pos + p.offset
 * ```
 * but when `p = bounds.size`, from (1):
 * ```
 * shift(bounds.size).pos + shift(bounds.size).offset = bounds.size.pos + bounds.size.offset
 * -> shift(bounds.size).pos + 0 = bounds.size.pos + bounds.size.offset
 * -> shift(bounds.size).pos = bounds.size.pos + bounds.size.offset
 * ```
 * so after shifting, the outermost point has position `bounds.size.pos + bounds.size.offset`
 * and offset `0`. Normalisation divides all points by the position of the
 * outermost point; therefore normalising shifted points will require us to
 * divide their positions by `bounds.size.pos + bounds.size.offset`.
 *
 * Now, consider (3):
 * ```
 * normalise_shifted(shift(p)).pos = normalise(p).pos
 * -> shift(p).pos / (bounds.size.pos + bounds.size.offset) = p.pos / bounds.size.pos
 * -> shift(p).pos = p.pos / bounds.size.pos * (bounds.size.pos + bounds.size.offset)
 * -> shift(p).pos = p.pos / bounds.size.pos * bounds.size.pos + p.pos / bounds.size.pos * bounds.size.offset
 * -> shift(p).pos = p.pos + p.pos / bounds.size.pos * bounds.size.offset
 * ```
 * and from (2), `shift(p).pos + shift(p).offset = p.pos + p.offset`, so
 * ```
 * shift(p).pos + shift(p).offset = p.pos + p.offset
 * -> p.pos + p.pos / bounds.size.pos * bounds.size.offset + shift(p).offset = p.pos + p.offset
 * -> p.pos / bounds.size.pos * bounds.size.offset + shift(p).offset = p.offset
 * -> shift(p).offset = p.offset - p.pos / bounds.size.pos * bounds.size.offset
 * ```
 * so when shifting, any point `p` will add `p.pos / bounds.size.pos * bounds.size.offset`
 * to its position, and subtract the same amount from its offset.
 */
function adjustPointSoBottomRightHasOffsetOfZero(
  p: GraphicPoint,
  boundsSize: ReadonlyGraphicPoint,
): void {
  const shiftX = (p.xPos() * boundsSize.xOffset()) / boundsSize.xPos();
  const shiftY = (p.yPos() * boundsSize.yOffset()) / boundsSize.yPos();

  const shift = GraphicPoint.set(pointFlyweight, shiftX, shiftY, -shiftX, -shiftY);
  GraphicPoint.add(p, p, shift);
}

/**
 * Determines the minimum and maximum coordinates used by points in fills,
 * strokes or the text area within the specified GraphicContent.
 *
 * @param content The GraphicContent containing the fills, strokes and optionally text area to find the bounds for
 * @returns The minimum and maximum coordinates
 */
function getBounds(content: GraphicContent): Bounds {
  const sections = [...content.fills, ...content.strokes];

  // Default to 0 -> 1 if the graphic is empty
  if (sections.length === 0) {
    return {
      min: GraphicPoint.ZERO,
      max: GraphicPoint.UNIT,
      size: GraphicPoint.UNIT,
    };
  }

  const min = GraphicPoint.copy(GraphicPoint.create(), sections[0].segments[0].start);
  const max = GraphicPoint.copy(GraphicPoint.create(), min);
  const resolvedMin = min.resolveAtUnitSize(vecFlyweight1);
  const resolvedMax = max.resolveAtUnitSize(vecFlyweight2);

  const checkPoint = (point: ReadonlyGraphicPoint) => {
    const resolvedX = point.resolveAtUnitSizeForAxis('x');
    if (resolvedX < resolvedMin[0]) {
      GraphicPoint.copyPointsByAxis(min, { x: point });
      resolvedMin[0] = resolvedX;
    } else if (resolvedX > resolvedMax[0]) {
      GraphicPoint.copyPointsByAxis(max, { x: point });
      resolvedMax[0] = resolvedX;
    }

    const resolvedY = point.resolveAtUnitSizeForAxis('y');
    if (resolvedY < resolvedMin[1]) {
      GraphicPoint.copyPointsByAxis(min, { y: point });
      resolvedMin[1] = resolvedY;
    } else if (resolvedY > resolvedMax[1]) {
      GraphicPoint.copyPointsByAxis(max, { y: point });
      resolvedMax[1] = resolvedY;
    }
  };

  for (const section of sections) {
    for (const segment of section.segments) {
      if (segment.type === 'cubicBezier') {
        // We choose an odd number for the resolution to include the middle
        // point of the bezier, which due to symmetry is often the min/max
        // point in some axis.
        createBezierFromDefinition(segment).walkPoints(9, checkPoint);
      } else if (segment.type === 'line') {
        checkPoint(segment.start);
        checkPoint(segment.end);
      }
    }
  }

  if (content.textArea) {
    checkPoint(content.textArea.topLeft);
    checkPoint(content.textArea.bottomRight);
  }

  return {
    min,
    max,
    size: GraphicPoint.subtract(GraphicPoint.create(), max, min),
  };
}

function readablePointToGraphicPoint(p: ReadablePoint) {
  const graphicPoint = GraphicPoint.create();

  if (typeof p[0] === 'number') {
    GraphicPoint.setForAxis(graphicPoint, 'x', p[0], 0);
  } else {
    GraphicPoint.setForAxis(graphicPoint, 'x', p[0].pos, p[0].offset);
  }

  if (typeof p[1] === 'number') {
    GraphicPoint.setForAxis(graphicPoint, 'y', p[1], 0);
  } else {
    GraphicPoint.setForAxis(graphicPoint, 'y', p[1].pos, p[1].offset);
  }

  return graphicPoint;
}

function graphicPointToReadablePoint(point: GraphicPoint): ReadablePoint {
  return [
    { pos: point.xPos(), offset: point.xOffset() },
    { pos: point.yPos(), offset: point.yOffset() },
  ];
}

// Definition examination helpers

/**
 * Gets only the segments of a section definition that are straight lines.
 *
 * @param section The fill or stroke definition to search
 * @returns A list of line definitions for the provided section
 */
export function getLinesForSection(
  section: GraphicSectionDefinition,
): GraphicLineDefinition[] {
  return section.segments.filter((segment) => segment.type === 'line');
}

/**
 * Gets only the segments of a section definition that are cubic bezier curves.
 *
 * @param section The fill or stroke definition to search
 * @returns A list of cubic bezier definitions for the provided section
 */
export function getBeziersForSection(
  section: GraphicSectionDefinition,
): GraphicCubicBezierDefinition[] {
  return section.segments.filter((segment) => segment.type === 'cubicBezier');
}

/**
 * Determine the point on a line definition with a given y-coordinate.
 *
 * @param edge The line definition
 * @param y The y-coordinate we want the point on the line for
 * @returns The point on the line with the provided y-coordinate
 */
export function getHorizontalIntersectionWithEdge(
  edge: GraphicLineDefinition,
  y: ReadablePointComponent,
  allowProjection = false,
): GraphicPoint {
  return getPointOnEdgeDefinitionFromValueOnAxis(edge, 'y', y, allowProjection);
}

/**
 * Determine the point on a line definition with a given x-coordinate.
 *
 * @param edge The line definition
 * @param x The x-coordinate we want the point on the line for
 * @returns The point on the line with the provided x-coordinate
 */
export function getVerticalIntersectionWithEdge(
  edge: GraphicLineDefinition,
  x: ReadablePointComponent,
  allowProjection = false,
): GraphicPoint {
  return getPointOnEdgeDefinitionFromValueOnAxis(edge, 'x', x, allowProjection);
}

function getPointOnEdgeDefinitionFromValueOnAxis(
  edge: GraphicLineDefinition,
  axis: Axis,
  value: ReadablePointComponent,
  allowProjection = false,
) {
  const result = getPointOnEdgeFromValueOnAxis(
    edge.start,
    edge.end,
    axis,
    value,
    allowProjection,
  );

  if ('failureReason' in result) {
    switch (result.failureReason) {
      case 'not representable':
        throw new NotRepresentableByGraphicPointError(
          `Proportion along edge from position ${result.posProportion} differs from proportion from offsets ${result.offsetProportion}, so cannot be represented as a GraphicPoint`,
        );
      case 'no intersection':
        throw new NoIntersectionWithEdgeError(
          `No point found on edge from ${edge.start} to ${edge.end} at ${axis} = ${value}`,
        );
    }
  }

  return result;
}

/**
 * Get the point on a bezier definition for a given t-value.
 *
 * @param bezier The bezier definition
 * @param t The position along the bezier, from 0 to 1
 * @returns The point on the bezier for the provided t-value
 */
export function getPointOnBezierDefinition(
  bezier: GraphicCubicBezierDefinition,
  t: number,
): GraphicPoint {
  return createBezierFromDefinition(bezier).getPoint(GraphicPoint.create(), t);
}

/**
 * Determines the approximate point on a bezier that is furthest along a given
 * axis, either positively or negatively. If there are multiple such points,
 * the point earliest in the curve is returned.
 *
 * @param bezier The bezier definition
 * @param direction The axis and direction to find the furthest point in
 * @param resolution The number of points along the bezier to test
 * @returns The point approximating the furthest point along the bezier in the provided direction
 */
export function getFurthestPointOnBezier(
  bezier: GraphicCubicBezierDefinition,
  direction: Direction,
  resolution: number,
): GraphicPoint {
  const axis = dirIsXAxis(direction) ? 'x' : 'y';
  const shouldMaximise = dirIsPositive(direction);

  const bestPoint = GraphicPoint.copy(GraphicPoint.create(), bezier.start);
  let resolvedBestPoint = bestPoint.resolveAtUnitSizeForAxis(axis);

  createBezierFromDefinition(bezier).walkPoints(resolution, (point) => {
    const resolvedPoint = point.resolveAtUnitSizeForAxis(axis);
    if (
      shouldMaximise
        ? resolvedPoint > resolvedBestPoint
        : resolvedPoint < resolvedBestPoint
    ) {
      GraphicPoint.copy(bestPoint, point);
      resolvedBestPoint = resolvedPoint;
    }
  });

  return bestPoint;
}

/**
 * Creates a cubic bezier that can be interpreted by our maths functions from
 * an appropriate segment definition for things like bounding calculations.
 *
 * @param bezier A segment definition from a fill or stroke that represents a cubic bezier
 * @returns An actual cubic bezier matching the provided segment definition
 */
function createBezierFromDefinition(
  bezier: GraphicCubicBezierDefinition,
): GraphicCubicBezier {
  return new GraphicCubicBezier(bezier.start, bezier.cp1, bezier.cp2, bezier.end);
}

function unresolvePointOnLine(
  p: ReadonlyVec2,
  line: GraphicLineDefinition,
): GraphicPoint {
  const resolvedStartX = line.start.resolveAtUnitSizeForAxis('x');
  const resolvedStartY = line.start.resolveAtUnitSizeForAxis('y');

  const dx = line.end.resolveAtUnitSizeForAxis('x') - resolvedStartX;
  const dy = line.end.resolveAtUnitSizeForAxis('y') - resolvedStartY;

  const tx = dx !== 0 ? (p[0] - resolvedStartX) / dx : 0.5;
  const ty = dy !== 0 ? (p[1] - resolvedStartY) / dy : 0.5;

  return GraphicPoint.interpolateByAxis(
    GraphicPoint.create(),
    line.start,
    line.end,
    vec2.fromValues(tx, ty),
  );
}

/**
 * Extends a line by projecting outwards by the specified length. If the line
 * is horizontal or vertical, the projection will be a non-scaling offset only,
 * otherwise it will use linear interpolation to give a point that falls on the
 * projected line regardless of shape size and line angle.
 *
 * @param line The line to project
 * @param length The length to project by. Negative values project from the
 * start of the line rather than the end.
 *
 * @returns A graphic point at the specified distance from the line endpoint
 */
function extendLine(line: GraphicLineDefinition, length: number): GraphicPoint {
  const start = length >= 0 ? line.start : line.end;
  const end = length >= 0 ? line.end : line.start;

  const dx = end.resolveAtUnitSizeForAxis('x') - start.resolveAtUnitSizeForAxis('x');
  const dy = end.resolveAtUnitSizeForAxis('y') - start.resolveAtUnitSizeForAxis('y');

  const angle = Math.atan2(dy, dx);

  if (GraphicPoint.isHorizontal(start, end)) {
    return GraphicPoint.set(
      GraphicPoint.create(),
      end.xPos(),
      end.yPos(),
      end.xOffset() + Math.abs(length) * Math.cos(angle),
      end.yOffset(),
    );
  } else if (GraphicPoint.isVertical(start, end)) {
    return GraphicPoint.set(
      GraphicPoint.create(),
      end.xPos(),
      end.yPos(),
      end.xOffset(),
      end.yOffset() + Math.abs(length) * Math.sin(angle),
    );
  }

  const tx = dx !== 0 ? (Math.abs(length) * Math.cos(angle)) / dx : 0;
  const ty = dy !== 0 ? (Math.abs(length) * Math.sin(angle)) / dy : 0;

  return GraphicPoint.interpolateByAxis(
    GraphicPoint.create(),
    start,
    end,
    vec2.fromValues(tx + 1, ty + 1),
  );
}
